/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import Accordion from '@ion/dls/src/components/accordion/ExpansionPanel';
import {CTALink} from '@ion/dls/src/components/buttons/CTALink';
import Slider from '@ion/dls/src/components/slider/Slider';
import {dlsAlt, dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {getLayerIndex} from '../helpers/MapViewUtils';
import {
  LayerSettingsHeader,
  LayerSettingsMarks as Marks,
  LayerSettingsProps,
  LayerSettingsStyles,
  LayerSettingsSubLayersProps
} from './LayerSettings';
import styles from './LayerChooser.module.scss';

// defines the prop definitions for the WeatherLayerSettings
export type WeatherLayerSettingsProps = LayerSettingsProps & {
  webTileLayer: __esri.WebTileLayer;
  onOpacityChange: Function;
  onToggleExpand?: Function;
  opacity: number;
  url: string;
  expandedLayer: string
};

// defines the styles used by the WeatherLayerSettings
const useStyles = makeStyles(theme => (LayerSettingsStyles()));

/**
 * @Legend
 *
 * Defines the Legend Tab / Section of the WeatherLayerSettings
 */
const Legend = ({opacity = 1, changeOpacity}: LayerSettingsSubLayersProps) => {
  const classes: any = useStyles();
  const [value, setValue] = useState(opacity);
  const snowRender   = [];
  const rainRender   = [];
  const mixRender    = [];

  useEffect(() => {
    setValue(opacity);
  }, [opacity]);

  useEffect(() => {
    changeOpacity(value);
  }, [value]);

  const handleChange = (event, value) => {
    setValue(value);
  };

  const {
    snowTrace,
    rainTrace,
    rain0p10,
    rain0p25,
    rain1p25,
    rain4p00,
    rain16,
    mixTrace,
    mix0p10,
    mix1p25,
    mix4p00
  } = dlsAlt('maps', 'legend', 'weather');
  const labels = translate('maps:weather', {returnObjects: true});
  const snow = [
    {color: snowTrace, title: labels.trace},
    {color: dlsColor('skyBlue'), title: labels['0p10']},
    {color: dlsColor('darkTeal'), title: labels['0p25']}
  ];
  const rain = [
    {color: rainTrace, title: labels.trace},
    {color: rain0p10, title: labels['0p10']},
    {color: rain0p25, title: labels['0p25']},
    {color: rain1p25, title: labels['1p25']},
    {color: rain4p00, title: labels['4p00']},
    {color: rain16, title: labels['16+']}
  ];
  const mix = [
    {color: mixTrace, title: labels.trace},
    {color: mix0p10, title: labels['0p10']},
    {color: mix1p25, title: labels['1p25']},
    {color: mix4p00, title: labels['4p00']},
    {color: dlsColor('white'), title: ''}
  ];

  snow.map(({title, color}) => {
    snowRender.push(
      <div className={classes.legendIcon} key={title}>
        <div style={{backgroundColor: `${color}`}} className={classes.square} />
        <label className={classes.legendLabel}>{`${title}`}</label>
      </div>
    );
  });
  rain.map(({title, color}) => {
    rainRender.push(
      <div className={classes.legendIcon} key={title}>
        <div style={{backgroundColor: `${color}`}} className={classes.square} />
        <label className={classes.legendLabel}>{`${title}`}</label>
      </div>
    );
  });
  mix.map(({title, color}) => {
    mixRender.push(
      <div className={classes.legendIcon} key={title}>
        <div style={{backgroundColor: `${color}`}} className={classes.square} />
        <label className={classes.legendLabel}>{`${title}`}</label>
      </div>
    );
  });
  return (
    <div className={classes.weatherContainer}>
      <div className={styles.subContainer}>
        <div>
          <CTALink className={classes.subLabel}>{labels.rain}</CTALink>
          {rainRender}
        </div>
        <div>
          <CTALink className={classes.subLabel}>{labels.mix}</CTALink>
          {mixRender}
        </div>
        <div>
          <CTALink className={classes.subLabel}>{labels.snow}</CTALink>
          {snowRender}
        </div>
      </div>
      <div className={styles.subLayersSlider}>
        <span className={styles.subLayersHeader}>{translate('maps:weather.opacity')}</span>
        <Slider step={0.1}
          marks={Marks}
          value={value}
          max={1}
          onSlide={function(e, val) { handleChange(e, val); }} />
      </div>
    </div>
  );
};

// defines the unique identifier for the WeatherLayerSettings
export const WEATHER_LAYER = 'weather';

/**
 * @WeatherLayerSettings
 *
 * The Weather Layer Settings provides a component which allows users to select
 * specific weather layer related attributes to be displayed within a DLSMap.
 *
 * The WeatherLayerSettings component represents the "Weather Layers" DLS feature:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/01_DLS_Components?node-id=1904%3A13962
 * ```typescript
 *
 *   <WeatherLayerSettings url={}
 *     isEnabled={}
 *     onToggle={}
 *     onOpacityChange={}
 *     opacity={}
 *     map={} />
 *
 * ```
 */
export const WeatherLayerSettings = ({
  map,
  url,
  layers,
  onToggle,
  webTileLayer,
  onOpacityChange,
  onToggleExpand,
  opacity,
  isEnabled,
  expandedLayer
}: WeatherLayerSettingsProps) => {
  const classes: any                = useStyles();
  const [defaultLayer, setSelected] = useState(isEnabled);
  const layerRef                    = useRef();
  const labels                      = translate('maps:weather', {returnObjects: true});
  const WebTileLayer                = webTileLayer;

  useEffect(() => {
    setSelected(isEnabled);
  }, [isEnabled]);

  // loads all required esri js 4 map modules ...
  const loadMapModules = function() {
    if (defaultLayer) {
      layerRef.current = new WebTileLayer({
        urlTemplate : url,
        title       : WEATHER_LAYER,
        id          : WEATHER_LAYER
      });
      map.layers.add(layerRef.current);
      map.layers.reorder(layerRef.current, getLayerIndex(map, WEATHER_LAYER));
      changeOpacity(opacity);
    } else {
      map.layers.remove(layerRef.current);
    }
  };

  useEffect(() => {
    loadMapModules();
  }, [defaultLayer]);

  const onToggleLayer = useCallback(({target:{checked}}) => {
    setSelected(checked);
    onToggle(WEATHER_LAYER, checked, labels.header);
  }, [layers]);

  const changeOpacity = useCallback(newValue => {
    if (layerRef.current) {
      (layerRef.current as any).opacity = newValue;
      onOpacityChange(newValue);
    }
  }, []);

  const Details = (
    <>
      <div className={classes.layers}>
        <div className={classes.subLayer}>
          <CTALink className={classes.button}>
            {labels.legend}
          </CTALink>
        </div>
      </div>
      <Legend changeOpacity={changeOpacity} opacity={opacity} />
    </>
  );

  return (
    <div className={classes.root}>
      <Grid container direction='row'>
        <Grid item xs={12} md={12}>
          <Paper className={classes.paper}>
            <Accordion children=''
              id={labels.header}
              expandedLayer={expandedLayer}
              onToggleExpand={onToggleExpand}
              header={<LayerSettingsHeader label={labels.header}
                isSelected={defaultLayer}
                onToggle={onToggleLayer}/>} details={Details}
              detailsRootOverride={classes.accordionDetails} />
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default WeatherLayerSettings;
